/**
 * Client service for sector operations registers, templates, and sector roles.
 * R39 A4.a (plan docs/plans/sector-operations.md §2 SO-D8, SO-P3–SO-P5, SO-P50, SO-P51, SO-P55; §4 ops_templates, ops_records; §6 A4.a).
 * Wraps callables for writes and listens to firestore ops_templates / ops_records for reads.
 */

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  type Unsubscribe,
  type QueryConstraint,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type { OpsSubscriptionAccessOptions } from './opsRostersService'
import type {
  OpsPersonRef,
  OpsSector,
  OpsSectorRole,
  OpsTemplate,
  OpsTemplateVersion,
  OpsTemplateVersionState,
  OpsRecord,
  OpsRecordException,
  OpsRecordState,
  OpsRecordCorrection,
  OpsTemplateCoSignConfig,
  OpsRecordCoSign,
  SetSectorRolesInput,
  SaveTemplateDraftInput,
  SubmitTemplateVersionInput,
  ApproveTemplateVersionInput,
  SubmitRecordInput,
  ReviewRecordInput,
  ReturnRecordInput,
  ApproveRecordInput,
  AddCorrectionInput,
  CoSignRecordInput,
  ProxyCoSignRecordInput,
} from '../types/ops'

export async function setSectorRoles(
  input: SetSectorRolesInput,
): Promise<{ workspaceId: string; uid: string; roleKeys: OpsSectorRole[] }> {
  return callable<
    SetSectorRolesInput,
    { workspaceId: string; uid: string; roleKeys: OpsSectorRole[] }
  >('setSectorRoles', input)
}

export async function saveTemplateDraft(
  input: SaveTemplateDraftInput,
): Promise<{ templateId: string; version: number; state: OpsTemplateVersionState }> {
  return callable<
    SaveTemplateDraftInput,
    { templateId: string; version: number; state: OpsTemplateVersionState }
  >('saveTemplateDraft', input)
}

export async function submitTemplateVersion(
  input: SubmitTemplateVersionInput,
): Promise<{ templateId: string; version: number; state: OpsTemplateVersionState }> {
  return callable<
    SubmitTemplateVersionInput,
    { templateId: string; version: number; state: OpsTemplateVersionState }
  >('submitTemplateVersion', input)
}

export async function approveTemplateVersion(
  input: ApproveTemplateVersionInput,
): Promise<{
  templateId: string
  version: number
  state: OpsTemplateVersionState
  selfApproved: boolean
}> {
  return callable<
    ApproveTemplateVersionInput,
    {
      templateId: string
      version: number
      state: OpsTemplateVersionState
      selfApproved: boolean
    }
  >('approveTemplateVersion', input)
}

export async function submitRecord(
  input: SubmitRecordInput,
): Promise<{
  recordId: string
  state: OpsRecordState
  templateVersion: number
  lateBy: number
  late: boolean
  hasExceptions: boolean
  exceptionCount: number
  replayed?: boolean
}> {
  return callable<
    SubmitRecordInput,
    {
      recordId: string
      state: OpsRecordState
      templateVersion: number
      lateBy: number
      late: boolean
      hasExceptions: boolean
      exceptionCount: number
      replayed?: boolean
    }
  >('submitRecord', input)
}

export async function reviewRecord(
  input: ReviewRecordInput,
): Promise<{ recordId: string; state: OpsRecordState }> {
  return callable<ReviewRecordInput, { recordId: string; state: OpsRecordState }>(
    'reviewRecord',
    input,
  )
}

export async function returnRecord(
  input: ReturnRecordInput,
): Promise<{ recordId: string; state: OpsRecordState }> {
  return callable<ReturnRecordInput, { recordId: string; state: OpsRecordState }>(
    'returnRecord',
    input,
  )
}

export async function approveRecord(
  input: ApproveRecordInput,
): Promise<{ recordId: string; state: OpsRecordState; selfApproved: boolean }> {
  return callable<
    ApproveRecordInput,
    { recordId: string; state: OpsRecordState; selfApproved: boolean }
  >('approveRecord', input)
}

export async function addCorrection(
  input: AddCorrectionInput,
): Promise<{
  correctionId: string
  recordId: string
  correctionCount: number
  diffCount: number
}> {
  return callable<
    AddCorrectionInput,
    {
      correctionId: string
      recordId: string
      correctionCount: number
      diffCount: number
    }
  >('addCorrection', input)
}

export async function coSignRecord(
  input: CoSignRecordInput,
): Promise<{
  recordId: string
  state: OpsRecordState
  coSignState: 'SIGNED' | 'DISPUTED'
}> {
  return callable<
    CoSignRecordInput,
    {
      recordId: string
      state: OpsRecordState
      coSignState: 'SIGNED' | 'DISPUTED'
    }
  >('coSignRecord', input)
}

export async function proxyCoSignRecord(
  input: ProxyCoSignRecordInput,
): Promise<{
  recordId: string
  state: OpsRecordState
  coSignState: 'PROXY_SIGNED'
}> {
  return callable<
    ProxyCoSignRecordInput,
    {
      recordId: string
      state: OpsRecordState
      coSignState: 'PROXY_SIGNED'
    }
  >('proxyCoSignRecord', input)
}

export interface GetFormFPrecheckInput {
  workspaceId: string
  assetId?: string
  doctorPersonRef?: OpsPersonRef
  dayKey?: string
  occurredAt?: string | number
}

export async function getFormFPrecheck(
  input: GetFormFPrecheckInput,
): Promise<{ ok: boolean; missing: string[] }> {
  return callable<GetFormFPrecheckInput, { ok: boolean; missing: string[] }>(
    'getFormFPrecheck',
    input,
  )
}

export interface SubscribeRecordsAwaitingCoSignOptions extends OpsSubscriptionAccessOptions {
  sector?: OpsSector
  placeId?: string
  callerUid?: string
  isRestricted?: boolean
  isEditor?: boolean
  isReviewer?: boolean
}

export function subscribeToRecordsAwaitingCoSign(
  workspaceId: string,
  userUid: string,
  options: SubscribeRecordsAwaitingCoSignOptions | undefined,
  callback: (records: OpsRecord[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId || !userUid || !callback) {
    callback?.([])
    return () => undefined
  }

  const recordsRef = collection(firestoreDb, 'ops_records')
  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('coSign.state', '==', 'AWAITING'),
    where('coSign.signerRef.uid', '==', userUid),
  ]

  if (options?.sector) {
    constraints.push(where('sector', '==', options.sector))
  }
  if (options?.placeId) {
    constraints.push(where('placeId', '==', options.placeId))
  }

  if (options?.isRestricted === true) {
    const callerUid = options.callerUid?.trim() || userUid.trim()
    if (!callerUid) {
      onError?.(new Error('callerUid is required for restricted subscribeToRecordsAwaitingCoSign'))
      return () => undefined
    }

    const qNullConstraints = [...constraints, where('readAccess', '==', null)]
    const qUidConstraints = [...constraints, where('readAccess', 'array-contains', callerUid)]

    let docsNull: OpsRecord[] = []
    let docsUid: OpsRecord[] = []

    const mergeAndEmit = () => {
      const map = new Map<string, OpsRecord>()
      for (const r of docsNull) {
        if (r.id) map.set(r.id, r)
      }
      for (const r of docsUid) {
        if (r.id) map.set(r.id, r)
      }
      const merged = Array.from(map.values())
      merged.sort((a, b) => {
        const aTime = typeof a.receivedAt === 'number' ? a.receivedAt : 0
        const bTime = typeof b.receivedAt === 'number' ? b.receivedAt : 0
        return bTime - aTime || (b.id ?? '').localeCompare(a.id ?? '')
      })
      callback(merged)
    }

    const unsubNull = onSnapshot(
      query(recordsRef, ...qNullConstraints),
      (snap) => {
        docsNull = snap.docs.map(mapRecordDoc)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    const unsubUid = onSnapshot(
      query(recordsRef, ...qUidConstraints),
      (snap) => {
        docsUid = snap.docs.map(mapRecordDoc)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const q = query(recordsRef, ...constraints)

  return onSnapshot(
    q,
    (snap) => {
      const docs = snap.docs.map(mapRecordDoc)
      docs.sort((a, b) => {
        const aTime = typeof a.receivedAt === 'number' ? a.receivedAt : 0
        const bTime = typeof b.receivedAt === 'number' ? b.receivedAt : 0
        return bTime - aTime || (b.id ?? '').localeCompare(a.id ?? '')
      })
      callback(docs)
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export function subscribeToTemplates(
  workspaceId: string,
  sector: OpsSector,
  callback: (templates: OpsTemplate[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const templatesRef = collection(firestoreDb, 'ops_templates')
  const q = query(
    templatesRef,
    where('workspaceId', '==', workspaceId),
    where('sector', '==', sector),
  )

  return onSnapshot(
    q,
    (snap) => {
      const templates: OpsTemplate[] = snap.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          workspaceId: data.workspaceId,
          sector: data.sector,
          name: data.name,
          title: data.title ?? data.name,
          kind: data.kind,
          safetyTagged: Boolean(data.safetyTagged),
          patientLinked: Boolean(data.patientLinked),
          lateAfterMinutes: typeof data.lateAfterMinutes === 'number' ? data.lateAfterMinutes : 60,
          coSign: (data.coSign as OpsTemplateCoSignConfig | null) ?? null,
          source: data.source ?? undefined,
          edition: data.edition ?? undefined,
          jurisdiction: data.jurisdiction ?? undefined,
          starter: Boolean(data.starter),
          starterKey: data.starterKey ?? data.starterSlug ?? undefined,
          currentVersion: data.currentVersion ?? null,
          archived: data.archived === true,
          createdAt: data.createdAt,
          createdBy: data.createdBy,
          updatedAt: data.updatedAt,
          updatedBy: data.updatedBy,
        }
      })
      callback(templates)
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

export function subscribeToTemplate(
  templateId: string,
  callback: (template: OpsTemplate | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!templateId) {
    callback(null)
    return () => undefined
  }

  const ref = doc(firestoreDb, 'ops_templates', templateId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        callback(null)
        return
      }
      const data = snap.data()
      callback({
        id: snap.id,
        workspaceId: data.workspaceId,
        sector: data.sector,
        name: data.name,
        title: data.title ?? data.name,
        kind: data.kind,
        safetyTagged: Boolean(data.safetyTagged),
        patientLinked: Boolean(data.patientLinked),
        lateAfterMinutes: typeof data.lateAfterMinutes === 'number' ? data.lateAfterMinutes : 60,
        coSign: (data.coSign as OpsTemplateCoSignConfig | null) ?? null,
        source: data.source ?? undefined,
        edition: data.edition ?? undefined,
        jurisdiction: data.jurisdiction ?? undefined,
        starter: Boolean(data.starter),
        starterKey: data.starterKey ?? data.starterSlug ?? undefined,
        currentVersion: data.currentVersion ?? null,
        archived: data.archived === true,
        retentionClass: data.retentionClass ?? undefined,
        createdAt: data.createdAt,
        createdBy: data.createdBy,
        updatedAt: data.updatedAt,
        updatedBy: data.updatedBy,
      })
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export interface SubscribeTemplateVersionsOptions {
  approvedOnly?: boolean
}

export function subscribeToTemplateVersions(
  templateId: string,
  callback: (versions: OpsTemplateVersion[]) => void,
  onError?: (error: Error) => void,
  options?: SubscribeTemplateVersionsOptions,
): Unsubscribe {
  if (!templateId) {
    callback([])
    return () => undefined
  }

  const versionsRef = collection(firestoreDb, 'ops_templates', templateId, 'versions')
  const constraints: QueryConstraint[] = []
  if (options?.approvedOnly) {
    constraints.push(where('state', '==', 'APPROVED'))
  }
  constraints.push(orderBy('version', 'asc'))
  const q = query(versionsRef, ...constraints)

  return onSnapshot(
    q,
    (snap) => {
      const versions: OpsTemplateVersion[] = snap.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          version: data.version,
          templateId: data.templateId,
          workspaceId: data.workspaceId,
          sector: data.sector,
          fields: Array.isArray(data.fields) ? data.fields : [],
          coSign: (data.coSign as OpsTemplateCoSignConfig | null) ?? null,
          state: data.state,
          effectiveFrom: data.effectiveFrom,
          approvedBy: data.approvedBy ?? null,
          approvedAt: data.approvedAt,
          selfApproved: Boolean(data.selfApproved),
          selfApprovalReason: data.selfApprovalReason ?? null,
          submittedBy: data.submittedBy ?? null,
          submittedAt: data.submittedAt,
          createdAt: data.createdAt,
          createdBy: data.createdBy,
          updatedAt: data.updatedAt,
          updatedBy: data.updatedBy,
        }
      })
      callback(versions)
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

export interface SubscribeRecordsOptions extends OpsSubscriptionAccessOptions {
  sector?: OpsSector
  templateId?: string
  placeId?: string
  state?: OpsRecordState
  receivedSince?: number
  callerUid?: string
  isRestricted?: boolean
  isEditor?: boolean
  isReviewer?: boolean
}

function mapRecordDoc(doc: { id: string; data: () => Record<string, unknown> }): OpsRecord {
  const data = doc.data()
  return {
    id: doc.id,
    workspaceId: data.workspaceId as string,
    sector: data.sector as OpsSector,
    templateId: data.templateId as string,
    templateVersion: data.templateVersion as number,
    placeId: data.placeId as string,
    shiftId: (data.shiftId as string | null) ?? null,
    state: data.state as OpsRecordState,
    asDraft: Boolean(data.asDraft),
    occurredAt: data.occurredAt as number,
    capturedAt: data.capturedAt as number,
    receivedAt: data.receivedAt as number,
    lateBy: (data.lateBy as number) ?? 0,
    late: Boolean(data.late),
    opId: data.opId as string,
    values: (data.values as Record<string, unknown>) ?? {},
    exceptions: Array.isArray(data.exceptions) ? (data.exceptions as OpsRecordException[]) : [],
    hasExceptions: Boolean(data.hasExceptions),
    coSign: (data.coSign as OpsRecordCoSign | null) ?? null,
    authorUid: data.authorUid as string,
    reviewerUid: (data.reviewerUid as string | null) ?? null,
    reviewedAt: (data.reviewedAt as number | null) ?? null,
    approverUid: (data.approverUid as string | null) ?? null,
    approvedAt: (data.approvedAt as number | null) ?? null,
    selfApproved: Boolean(data.selfApproved),
    selfApprovalReason: (data.selfApprovalReason as string | null) ?? null,
    returnedReason: (data.returnedReason as string | null) ?? null,
    returnedBy: (data.returnedBy as string | null) ?? null,
    returnedAt: (data.returnedAt as number | null) ?? null,
    lockedAt: (data.lockedAt as number | null) ?? null,
    correctionCount: (data.correctionCount as number) ?? 0,
    lastCorrectedAt: (data.lastCorrectedAt as number | null) ?? null,
    readAccess: Array.isArray(data.readAccess) ? (data.readAccess as string[]) : [],
    createdAt: data.createdAt as string,
    updatedAt: data.updatedAt as string,
  }
}

export function subscribeToRecords(
  workspaceId: string,
  options: SubscribeRecordsOptions,
  callback: (records: OpsRecord[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const recordsRef = collection(firestoreDb, 'ops_records')
  const commonConstraints: QueryConstraint[] = [where('workspaceId', '==', workspaceId)]
  if (options.sector) {
    commonConstraints.push(where('sector', '==', options.sector))
  }
  if (options.templateId) {
    commonConstraints.push(where('templateId', '==', options.templateId))
  }
  if (options.placeId) {
    commonConstraints.push(where('placeId', '==', options.placeId))
  }
  if (options.state) {
    commonConstraints.push(where('state', '==', options.state))
  }
  if (typeof options.receivedSince === 'number') {
    commonConstraints.push(where('receivedAt', '>=', options.receivedSince))
  }

  const isEditorOrReviewer =
    options.isEditor === true ||
    options.isReviewer === true ||
    options.isElevated === true

  if (options.isRestricted === true) {
    const callerUid = options.callerUid?.trim()
    if (!callerUid) {
      onError?.(new Error('callerUid is required for restricted subscribeToRecords'))
      return () => undefined
    }

    const qNullConstraints = [...commonConstraints, where('readAccess', '==', null)]
    const qUidConstraints = [...commonConstraints, where('readAccess', 'array-contains', callerUid)]

    if (!isEditorOrReviewer) {
      qNullConstraints.push(where('authorUid', '==', callerUid))
      qUidConstraints.push(where('authorUid', '==', callerUid))
    }

    qNullConstraints.push(orderBy('receivedAt', 'desc'))
    qUidConstraints.push(orderBy('receivedAt', 'desc'))

    let docsNull: OpsRecord[] = []
    let docsUid: OpsRecord[] = []

    const mergeAndEmit = () => {
      const map = new Map<string, OpsRecord>()
      for (const r of docsNull) {
        if (r.id) map.set(r.id, r)
      }
      for (const r of docsUid) {
        if (r.id) map.set(r.id, r)
      }
      const merged = Array.from(map.values())
      merged.sort((a, b) => {
        const aTime = typeof a.receivedAt === 'number' ? a.receivedAt : 0
        const bTime = typeof b.receivedAt === 'number' ? b.receivedAt : 0
        return bTime - aTime || (b.id ?? '').localeCompare(a.id ?? '')
      })
      callback(merged)
    }

    const unsubNull = onSnapshot(
      query(recordsRef, ...qNullConstraints),
      (snap) => {
        docsNull = snap.docs.map(mapRecordDoc)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    const unsubUid = onSnapshot(
      query(recordsRef, ...qUidConstraints),
      (snap) => {
        docsUid = snap.docs.map(mapRecordDoc)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const constraints = [...commonConstraints]
  if (!isEditorOrReviewer && options.callerUid?.trim()) {
    constraints.push(where('authorUid', '==', options.callerUid.trim()))
  }
  constraints.push(orderBy('receivedAt', 'desc'))

  const q = query(recordsRef, ...constraints)

  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map(mapRecordDoc))
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

/**
 * Returns the most recent LOCKED or APPROVED record of a template at a place,
 * respecting caller restriction scoping (R39 F2.fix1 Item 3).
 */
export async function getLatestLockedRecord(
  workspaceId: string,
  templateId: string,
  placeId: string,
  options?: OpsSubscriptionAccessOptions & { isEditor?: boolean; isReviewer?: boolean },
): Promise<OpsRecord | null> {
  if (!workspaceId || !templateId || !placeId) {
    return null
  }

  const recordsRef = collection(firestoreDb, 'ops_records')
  const commonConstraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('templateId', '==', templateId),
    where('placeId', '==', placeId),
  ]

  const isEditorOrReviewer =
    options?.isEditor === true ||
    options?.isReviewer === true ||
    options?.isElevated === true

  let rawDocs: OpsRecord[]

  if (options?.isRestricted === true) {
    const callerUid = options.callerUid?.trim()
    if (!callerUid) {
      return null
    }

    const qNullConstraints = [...commonConstraints, where('readAccess', '==', null)]
    const qUidConstraints = [...commonConstraints, where('readAccess', 'array-contains', callerUid)]

    if (!isEditorOrReviewer) {
      qNullConstraints.push(where('authorUid', '==', callerUid))
      qUidConstraints.push(where('authorUid', '==', callerUid))
    }

    qNullConstraints.push(orderBy('receivedAt', 'desc'), limit(20))
    qUidConstraints.push(orderBy('receivedAt', 'desc'), limit(20))

    const [snapNull, snapUid] = await Promise.all([
      getDocs(query(recordsRef, ...qNullConstraints)),
      getDocs(query(recordsRef, ...qUidConstraints)),
    ])

    const map = new Map<string, OpsRecord>()
    for (const d of snapNull.docs) {
      const rec = mapRecordDoc(d)
      if (rec.id) map.set(rec.id, rec)
    }
    for (const d of snapUid.docs) {
      const rec = mapRecordDoc(d)
      if (rec.id) map.set(rec.id, rec)
    }
    rawDocs = Array.from(map.values())
  } else {
    const constraints = [...commonConstraints]
    if (!isEditorOrReviewer && options?.callerUid?.trim()) {
      constraints.push(where('authorUid', '==', options.callerUid.trim()))
    }
    constraints.push(orderBy('receivedAt', 'desc'), limit(20))
    const snap = await getDocs(query(recordsRef, ...constraints))
    rawDocs = snap.docs.map(mapRecordDoc)
  }

  const matching = rawDocs.filter((r) => {
    return r.state === 'APPROVED' || (r.state as string) === 'LOCKED' || Boolean(r.lockedAt)
  })

  if (matching.length === 0) {
    return null
  }

  matching.sort((a, b) => {
    const aTime = typeof a.receivedAt === 'number' ? a.receivedAt : (typeof a.occurredAt === 'number' ? a.occurredAt : 0)
    const bTime = typeof b.receivedAt === 'number' ? b.receivedAt : (typeof b.occurredAt === 'number' ? b.occurredAt : 0)
    return bTime - aTime || (b.id ?? '').localeCompare(a.id ?? '')
  })

  return matching[0] ?? null
}

export function subscribeToRecordCorrections(
  recordId: string,
  callback: (corrections: OpsRecordCorrection[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!recordId) {
    callback([])
    return () => undefined
  }

  const correctionsRef = collection(firestoreDb, 'ops_records', recordId, 'corrections')
  const q = query(correctionsRef, orderBy('createdAt', 'asc'))

  return onSnapshot(
    q,
    (snap) => {
      const corrections: OpsRecordCorrection[] = snap.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          recordId: data.recordId,
          workspaceId: data.workspaceId,
          reason: data.reason,
          authorUid: data.authorUid,
          authorName: data.authorName ?? undefined,
          createdAt: data.createdAt,
          changedValues: data.changedValues ?? {},
          diffs: Array.isArray(data.diffs) ? data.diffs : [],
        }
      })
      callback(corrections)
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

export function subscribeToSectorRoles(
  workspaceId: string,
  callback: (roles: Array<{ uid: string; roleKeys: OpsSectorRole[] }>) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const rolesRef = collection(firestoreDb, 'ops_sector_roles')
  const q = query(rolesRef, where('workspaceId', '==', workspaceId))

  return onSnapshot(
    q,
    (snap) => {
      const roles = snap.docs.map((doc) => {
        const data = doc.data()
        return {
          uid: data.uid,
          roleKeys: (Array.isArray(data.roleKeys) ? data.roleKeys : []) as OpsSectorRole[],
        }
      })
      callback(roles)
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

export interface MySectorRolesResult {
  roleKeys: OpsSectorRole[]
  exportSectors: OpsSector[]
}

export function subscribeToMySectorRoles(
  workspaceId: string,
  uid: string,
  callback: (roles: MySectorRolesResult) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId || !uid) {
    callback({ roleKeys: [], exportSectors: [] })
    return () => undefined
  }

  const roleDocRef = doc(firestoreDb, 'ops_sector_roles', `${workspaceId}_${uid}`)

  return onSnapshot(
    roleDocRef,
    (snap) => {
      if (!snap.exists()) {
        callback({ roleKeys: [], exportSectors: [] })
        return
      }
      const data = snap.data()
      const roleKeys = (Array.isArray(data?.roleKeys) ? data.roleKeys : []) as OpsSectorRole[]
      const exportSectors = (Array.isArray(data?.exportSectors) ? data.exportSectors : []) as OpsSector[]
      callback({ roleKeys, exportSectors })
    },
    (err) => {
      const errObj = err instanceof Error ? err : new Error(String(err))
      const code = (err as { code?: string }).code
      const msg = errObj.message.toLowerCase()
      if (code === 'permission-denied' || msg.includes('permission')) {
        callback({ roleKeys: [], exportSectors: [] })
      }
      onError?.(errObj)
    },
  )
}

